import { computeBackgroundMetrics, type BackgroundOptions } from '../background/analyze.js';
import { DEFAULT_CHECKS } from '../checks/index.js';
import { refineHeadTopFromSilhouette } from '../geometry/headTop.js';
import { computeFaceGeometry, type FaceGeometryOptions } from '../geometry/faceGeometry.js';
import { estimateVisibleChin } from '../geometry/chinContour.js';
import { ringLuma, topBandLuma } from '../image/analysis.js';
import { nowMs } from '../utils/clock.js';
import { computeQualityMetrics } from '../quality/index.js';
import { createProfileRegistry } from '../rules/profile.js';
import { createRuleEngine } from '../rules/ruleEngine.js';
import { categoryOf } from '../types/checks.js';
import { buildReport, createReportId } from '../reports/reportBuilder.js';
import type { CheckResult, FileInfo, ValidationCheck } from '../types/checks.js';
import type { FaceObservation, FaceObservations } from '../types/observations.js';
import { EMPTY_FACE_OBSERVATIONS } from '../types/observations.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';
import type { DocumentProfile, ImageRequirements, ProfileRegistry } from '../types/profile.js';
import type { ValidationEngineInfo, ValidationInput, ValidationReport } from '../types/result.js';

/**
 * The validator engine.
 *
 * Pipeline (each stage is optional and its absence is reported honestly):
 *
 *   file guard → technical → face geometry (+ silhouette head top)
 *              → quality → background → rules → report
 *
 * The engine is document-agnostic: documents arrive as profile objects, checks
 * arrive as a registry. Nothing in this file — or anywhere else in the core —
 * knows the name of a single document.
 */

export const CORE_VERSION = '1.0.0';

export interface DocumentSummary {
  id: string;
  name: string;
  shortName?: string;
  country?: string;
  authority?: string;
  sourceUrl: string;
  sourceCheckedAt: string;
  disclaimer: string;
  aliases: string[];
  slug?: string;
  seoAliases: string[];
  image: ImageRequirements;
  visual: DocumentProfile['visual'];
  cta: DocumentProfile['cta'];
  enabledChecks: string[];
}

export interface ValidatorOptions {
  profiles: readonly DocumentProfile[];
  checks?: readonly ValidationCheck[];
  /** Head-top estimation tuning, forwarded to the geometry stage. */
  geometry?: FaceGeometryOptions;
  /** Background analysis tuning. */
  background?: Partial<BackgroundOptions>;
  /** Long side of the analysis buffer used by the quality/background stages. */
  analysisMaxSide?: number;
  engineName?: string;
  engineVersion?: string;
}

export interface Validator {
  readonly documents: ProfileRegistry;
  listDocuments(): DocumentSummary[];
  getDocument(idOrAlias: string): DocumentProfile | undefined;
  validate(input: ValidationInput): ValidationReport;
}



/** Largest, most central face wins when several were detected. */
export const selectPrimaryFace = (faces: readonly FaceObservation[]): FaceObservation | null => {
  if (faces.length === 0) return null;
  let best: FaceObservation | null = null;
  let bestScore = -1;
  for (const face of faces) {
    const box = face.box ?? meshBoxOf(face);
    if (!box) {
      if (best === null) best = face;
      continue;
    }
    const area = box.width * box.height;
    const centerX = box.x + box.width / 2;
    const centerY = box.y + box.height / 2;
    const centrality = 1 - Math.min(1, Math.hypot(centerX - 0.5, centerY - 0.5));
    const score = area * (0.7 + centrality * 0.3) * (0.5 + face.confidence * 0.5);
    if (score > bestScore) {
      bestScore = score;
      best = face;
    }
  }
  return best;
};

const meshBoxOf = (face: FaceObservation): { x: number; y: number; width: number; height: number } | null => {
  if (face.landmarks.length === 0) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const landmark of face.landmarks) {
    if (!Number.isFinite(landmark.x) || !Number.isFinite(landmark.y)) continue;
    minX = Math.min(minX, landmark.x);
    minY = Math.min(minY, landmark.y);
    maxX = Math.max(maxX, landmark.x);
    maxY = Math.max(maxY, landmark.y);
  }
  if (!Number.isFinite(minX)) return null;
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

/**
 * Cheap local noise estimate around a point (median-of-9 residual, MAD-scaled).
 *
 * Sampling a small window instead of the whole frame keeps the fast pass fast
 * while still protecting the silhouette step from grain.
 */
const estimateLocalNoiseSigma = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  point: { x: number; y: number },
  window = 48,
): number => {
  const centreX = Math.round(point.x * width);
  const centreY = Math.round(point.y * height);
  const x0 = Math.max(1, centreX - window);
  const x1 = Math.min(width - 2, centreX + window);
  const y0 = Math.max(1, centreY - window);
  const y1 = Math.min(height - 2, centreY + window);
  const residuals: number[] = [];
  const neighbourhood = new Array<number>(9);
  const stepX = Math.max(1, Math.floor((x1 - x0) / 32));
  const stepY = Math.max(1, Math.floor((y1 - y0) / 32));

  for (let y = y0; y <= y1; y += stepY) {
    for (let x = x0; x <= x1; x += stepX) {
      let index = 0;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          neighbourhood[index] = luma[(y + dy) * width + (x + dx)] as number;
          index += 1;
        }
      }
      const sorted = [...neighbourhood].sort((a, b) => a - b);
      residuals.push((luma[y * width + x] as number) - (sorted[4] as number));
    }
  }
  if (residuals.length === 0) return 0;
  residuals.sort((a, b) => a - b);
  const mad = residuals[Math.floor(residuals.length / 2)] as number;
  return Math.abs(mad) * 1.4826;
};

const parseHexColor = (value: unknown): { r: number; g: number; b: number } | undefined => {
  if (typeof value !== 'string') return undefined;
  const match = /^#?([0-9a-f]{6})$/i.exec(value.trim());
  if (!match) return undefined;
  const hex = match[1] as string;
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
};

export const summarizeDocument = (profile: DocumentProfile): DocumentSummary => ({
  id: profile.id,
  name: profile.name,
  ...(profile.shortName !== undefined ? { shortName: profile.shortName } : {}),
  ...(profile.country !== undefined ? { country: profile.country } : {}),
  ...(profile.authority !== undefined ? { authority: profile.authority } : {}),
  sourceUrl: profile.sourceUrl,
  sourceCheckedAt: profile.sourceCheckedAt,
  disclaimer: profile.disclaimer,
  aliases: profile.aliases ?? [],
  ...(profile.seo?.slug !== undefined ? { slug: profile.seo.slug } : {}),
  seoAliases: profile.seo?.aliases ?? [],
  image: profile.image,
  visual: profile.visual,
  cta: profile.cta,
  enabledChecks: Object.entries(profile.rules)
    .filter(([, rule]) => rule.enabled)
    .map(([id]) => id),
});

export const createValidator = (options: ValidatorOptions): Validator => {
  const profiles = [...options.profiles];
  const registry = createProfileRegistry(profiles);
  const engine = createRuleEngine({ checks: options.checks ?? DEFAULT_CHECKS });
  const analysisMaxSide = options.analysisMaxSide ?? 720;

  const validate = (input: ValidationInput): ValidationReport => {
    const startedAt = nowMs();
    const now = input.now ?? new Date();
    const location = input.location ?? 'browser';
    const profile = input.profile;
    const faces: FaceObservations = input.faces ?? EMPTY_FACE_OBSERVATIONS;
    const fileInfo: FileInfo = input.fileInfo ?? {};
    const pixels = input.pixels;

    const stages: string[] = ['file_guard', 'technical'];
    const skippedStages: Array<{ stage: string; reason: string }> = [];
    const warnings: string[] = [];

    // ---------------------------------------------------------- geometry
    let geometry: FaceGeometryMetrics | undefined;
    if (faces.faces.length > 0) {
      const primary = selectPrimaryFace(faces.faces);
      if (primary) {
        const imageSize = { width: input.image.width, height: input.image.height };
        const firstPass = computeFaceGeometry(primary, {
          ...(options.geometry ?? {}),
          imageSize,
        });
        if (firstPass.metrics && pixels) {
          const backgroundLuma = ringLuma(pixels.luma, pixels.width, pixels.height);
          /**
           * The silhouette step must not mistake sensor grain for a hairline:
           * the local noise sigma around the forehead sets the contrast floor.
           */
          const noiseSigma = estimateLocalNoiseSigma(
            pixels.luma,
            pixels.width,
            pixels.height,
            firstPass.metrics.foreheadPoint,
          );
          const silhouette = refineHeadTopFromSilhouette({
            luma: pixels.luma,
            width: pixels.width,
            height: pixels.height,
            columnX: firstPass.metrics.foreheadPoint.x,
            /**
             * The scan must start *above* the extrapolated head top, never at the
             * hairline. Starting at the forehead made the hairline the first
             * candidate edge, and on dark hair against a dark background that
             * turned a 60 % head into a 44 % one.
             */
            startY: Math.max(0.004, firstPass.metrics.headTopPoint.y - 0.01),
            backgroundLuma,
            /**
             * The background above the head, not the whole-frame ring: on a real
             * portrait the bottom strips are clothing, which dragged the reference
             * ~70 luma away from the actual background and made the scan report
             * "no background above" on every frame.
             */
            backgroundLumaAlt: topBandLuma(pixels.luma, pixels.width, pixels.height),
            noiseSigma,
          });
          /**
           * Diagnostic only: the visible chin contour measured from the pixels.
           * `chinPoint`, `headHeight` and every verdict keep using landmark 152 —
           * this value is carried into the metrics/audit for calibration and is
           * never read by a check.
           */
          const visibleChin = estimateVisibleChin({
            luma: pixels.luma,
            data: pixels.data,
            channels: pixels.channels,
            width: pixels.width,
            height: pixels.height,
            chinY: firstPass.metrics.chinPoint.y,
            faceCenterX: firstPass.metrics.faceCenterX,
          });
          const refined = computeFaceGeometry(primary, {
            ...(options.geometry ?? {}),
            imageSize,
            silhouette,
            visibleChin,
          });
          geometry = refined.metrics ?? firstPass.metrics ?? undefined;
          if (silhouette.found) stages.push('head_top_silhouette');
        } else {
          geometry = firstPass.metrics ?? undefined;
        }
        if (!geometry) {
          warnings.push(firstPass.reason ?? 'Геометрию лица рассчитать не удалось.');
        }
      }
    } else {
      skippedStages.push({
        stage: 'geometry',
        reason: 'Лицо не обнаружено: геометрические проверки не выполнялись.',
      });
    }
    if (geometry) stages.push('geometry');

    // ------------------------------------------------- quality/background
    let quality: ReturnType<typeof computeQualityMetrics> | undefined;
    let background: ReturnType<typeof computeBackgroundMetrics> | undefined;

    if (pixels && !input.fastOnly) {
      quality = computeQualityMetrics(pixels);
      stages.push('quality');

      if (geometry) {
        const targetColor = parseHexColor(
          (profile.rules.background?.params as Record<string, unknown> | undefined)?.['targetColor'],
        );
        background = computeBackgroundMetrics({
          buffer: pixels,
          subjectRect: geometry.headBox,
          ...(targetColor ? { targetColor } : {}),
          ...(options.background ? { options: options.background } : {}),
        });
        stages.push('background');
      } else {
        skippedStages.push({
          stage: 'background',
          reason: 'Без геометрии лица невозможно отделить фон от объекта съёмки.',
        });
      }
    } else if (input.fastOnly) {
      skippedStages.push({
        stage: 'quality',
        reason: 'Быстрый режим: анализ качества и фона выполняется вторым проходом.',
      });
    } else {
      skippedStages.push({
        stage: 'quality',
        reason: 'Пиксельные данные недоступны: изображение не было декодировано.',
      });
    }

    // ------------------------------------------------------------- checks
    const context = {
      image: input.image,
      ...(pixels ? { pixels } : {}),
      fileInfo,
      faces,
      ...(geometry ? { geometry } : {}),
      ...(quality ? { quality } : {}),
      ...(background ? { background } : {}),
      now,
    };
    const maxStage = input.maxStage ?? 'advanced';
    const checks: CheckResult[] = engine.runAll(profile, context, {
      // The authenticity ensemble is the most expensive stage; the interface
      // shows the fast and deep results first and adds this one afterwards.
      ...(maxStage === 'advanced'
        ? {}
        : { skip: (check: ValidationCheck) => categoryOf(check.id) === 'authenticity' }),
    });
    stages.push('rules');

    const finishedAt = nowMs();
    const engineInfo: ValidationEngineInfo = {
      name: options.engineName ?? 'validator-core',
      version: options.engineVersion ?? CORE_VERSION,
      location,
      stages,
      skippedStages,
      // Filled by the report builder from the `not_checked` checks.
      capabilityIssues: [],
    };

    const report = buildReport({
      profile,
      checks,
      image: input.image,
      fileInfo,
      faces,
      geometry,
      quality,
      background,
      engine: engineInfo,
      startedAt,
      finishedAt,
      now,
      warnings,
      location,
      reportId: createReportId(),
    });

    if (analysisMaxSide < 0) {
      warnings.push('Некорректное значение analysisMaxSide.');
    }

    return report;
  };

  return {
    documents: registry,
    listDocuments: () => profiles.map(summarizeDocument),
    getDocument: (idOrAlias) => registry.get(idOrAlias),
    validate,
  };
};

/**
 * Convenience wrapper for tests and for callers that do not need a shared
 * validator instance. Equivalent to `createValidator({profiles}).validate(...)`.
 */
export const validate = (
  input: ValidationInput & { profiles: readonly DocumentProfile[] },
): ValidationReport => {
  const validator = createValidator({ profiles: input.profiles });
  return validator.validate(input);
};
